/**
 * Where a dashboard card goes when it is clicked, as one definition.
 *
 * Every dashboard (admin, restaurant, shop and wholesaler, pharmacy, brand,
 * 3PL partner) shows the same kind of cards - new, pending delivery,
 * assigned, delivered, one per payment method - and each card is a count the
 * server made with one filter. The card must open the orders list with THAT
 * filter, so the number on the card is the number of rows that open. This
 * module is where card and list agree; the server reads the same parameter
 * names in utils/orderListFilters.js.
 *
 * `ticket` narrows to ticket orders by their own cycle (sold, attention, ...;
 * server: ticketStateClause).
 *
 *   ordersPath(audience)              the list a vendor type works from
 *   ordersLink(audience, filters)     that list, filtered
 *   cardLink(audience, card)          the link one dashboard card means
 *   CARD_FILTERS                      the filter each card counts by
 *
 * Filters are plain query parameters: `delivery` is one or more
 * delivery_status values, `method` one or more paymentMethod values (the
 * tender, as in the COD and MoMo cards). A list is sent as a comma list.
 */

export const ORDERS_PATH_BY_AUDIENCE = {
  admin: "/admin/paginatedOrdersTable",
  restaurant: "/admin/resOrdersTable",
  retailer: "/admin/retailerWholesalerOrdersTable",
  healthcare: "/admin/healthcare-orders",
  brand: "/admin/brand-quotes",
  partner: "/admin/partner-orders",
};

/* The brand dashboard counts three statuses as "pending delivery" (server:
   brandController BRAND_PENDING_DELIVERY_STATUSES); everyone else counts
   DISPATCHED alone. */
export const BRAND_PENDING_DELIVERY = ["ASSIGNED", "DISPATCHED", "IN_TRANSIT"];

export const CARD_FILTERS = {
  new: { delivery: "PROCESSING" },
  // Ticket orders (16 Oct 2026). The sales card adds `from` (the server's
  // `ticketSalesSince`), so it opens exactly the window it counted.
  ticketSales: { ticket: "sold" },
  ticketAttention: { ticket: "attention" },
  pending: { delivery: "DISPATCHED" },
  assigned: { delivery: "ASSIGNED" },
  delivered: { delivery: "DELIVERED" },
  cod: { method: "COD" },
  momo: { method: "MOMO" },
};

/** The orders list for one audience. Unknown audiences go to the admin list. */
export const ordersPath = (audience) => ORDERS_PATH_BY_AUDIENCE[audience] || ORDERS_PATH_BY_AUDIENCE.admin;

const joinList = (value) => (Array.isArray(value) ? value : [value]).map((v) => String(v).trim()).filter(Boolean).join(",");

/** `filters` as a query string, empty values left out, lists comma-joined. */
export const filtersToQuery = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    const text = joinList(value == null ? [] : value);
    if (text) params.set(key, text);
  });
  return params.toString();
};

/** The orders list for `audience`, narrowed by `filters`. */
export const ordersLink = (audience, filters = {}) => {
  const qs = filtersToQuery(filters);
  return qs ? `${ordersPath(audience)}?${qs}` : ordersPath(audience);
};

/**
 * The link one dashboard card means for one audience. `card` is a key of
 * CARD_FILTERS, or `{ method }` for a payment-method chip. The brand
 * dashboard's pending card carries its own wider status list.
 */
export const cardLink = (audience, card) => {
  if (card && typeof card === "object") return ordersLink(audience, card);
  if (card === "pending" && audience === "brand") return ordersLink(audience, { delivery: BRAND_PENDING_DELIVERY });
  return ordersLink(audience, CARD_FILTERS[card] || {});
};

/** The `delivery` list from a query string, upper-cased, in order. */
export const deliveryListOf = (search) => (new URLSearchParams(search).get("delivery") || "")
  .split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);

/**
 * Props that make any element behave as the card's link: pointer, keyboard
 * activation, a role for screen readers. Spread onto the card root.
 */
export const cardLinkProps = (go, label) => ({
  role: "link",
  tabIndex: 0,
  "aria-label": label,
  onClick: go,
  onKeyDown: (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      go();
    }
  },
});
